import { CaretRightFilled } from '@ant-design/icons';
import { ChevronRight } from 'lucide-react';
import {
  cloneElement,
  type CSSProperties,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import styled from 'styled-components';

import TooltipV2 from '@/components/Tooltip/TooltipV2';
import useColor from '@/hooks/domain/useColor';

import { Flex } from '../base';
import { Option } from '../Select/styled';
import Typo from '../Typography/Typo';
import Menu from './Menu';
import type { MenuItemProps as MenuItemT } from './types';

export type ActionMenuT = {
  id: string;
  icon?: ReactElement;
  node: ReactNode;
  disabled?: boolean;
  tooltip?: ReactNode;
  className?: string;
  shortcut?: {
    keyCode: string;
    keyName: string;
    callback: (el: HTMLElement) => void;
  };
  hasSubMenu?: boolean;
  onClick?: (e: MouseEvent) => void;
  subMenus?: MenuItemT[];
  subMenuWidth?: CSSProperties['width'];
  subMenuOptions?: {
    isOpen: boolean;
    openMenu: () => void;
    closeMenu: () => void;
  };
  closeMenu?: () => void;
};

const MenuItem = (props: ActionMenuT) => {
  const color = useColor();

  const icon = props.icon
    ? cloneElement(props.icon, {
        style: {
          width: 12,
          height: 12,
          color: color.gray600,
        },
      })
    : null;
  if (props.subMenus) {
    return (
      <Menu
        modalWidth={props.subMenuWidth}
        isOpen={props.subMenuOptions?.isOpen}
        openMenu={props.subMenuOptions?.openMenu}
        closeMenu={() => {
          props.subMenuOptions?.closeMenu();
          props.closeMenu?.();
        }}
        triggerNode={
          <Option
            j="space-between"
            className={props.className}
            data-action-menu-id={props.id}
            style={{ height: 32 }}
            onClick={
              !props.disabled
                ? (e) => {
                    e.stopPropagation();
                    props.onClick?.(e);
                    props.closeMenu?.();
                  }
                : undefined
            }
            disabled={props.disabled}
          >
            <Flex f={1} g={6} a="center">
              {icon}
              <Typo type="label2">{props.node}</Typo>
            </Flex>
            <Flex a="center" g={6}>
              {props.shortcut && (
                <ShortcutStyle>
                  <Typo type="label3" style={{ lineHeight: 1 }}>
                    {props.shortcut?.keyName}
                  </Typo>
                </ShortcutStyle>
              )}

              <ChevronRight className="size-3.5 text-gray-600" />
            </Flex>
          </Option>
        }
        options={props.subMenus}
        modalOptions={{
          direction: 'right',
          offset: {
            x: 8,
            y: -4,
          },
        }}
      />
    );
  }
  const option = (
    <Option
      className={props.className}
      j="space-between"
      data-action-menu-id={props.id}
      style={{ height: 32 }}
      onClick={
        !props.disabled
          ? (e) => {
              e.stopPropagation();
              props.onClick?.(e);
              props.closeMenu?.();
            }
          : undefined
      }
      disabled={props.disabled}
    >
      <Flex f={1} g={6} a="center">
        {icon}
        <Typo type="label2">{props.node}</Typo>
      </Flex>
      <Flex a="center" g={6}>
        {props.shortcut && (
          <ShortcutStyle>
            <Typo type="label3" style={{ lineHeight: 1 }}>
              {props.shortcut?.keyName}
            </Typo>
          </ShortcutStyle>
        )}

        {props.hasSubMenu && (
          <CaretRightFilled style={{ color: color.gray600 }} />
        )}
      </Flex>
    </Option>
  );

  return props.tooltip ? (
    <TooltipV2 tooltipNode={props.tooltip}>{option}</TooltipV2>
  ) : (
    option
  );
};

const ShortcutStyle = styled(Flex)`
  padding: 2px 4px;
  border-radius: 4px;
  border: 1px solid ${(props) => props.theme.colors.gray400};
`;

export default MenuItem;
